import { format } from 'date-fns';
import { keys } from '#helpers/otherHelpers.ts';
import { type Config, type DiscoverMovieFilters, type DiscoverTvFilters } from '#types.ts';
import { type Mark } from '../../Form/RangeSlider/types.ts';
import { type CompanyOption } from '../CompanyMultiSelectDropdown/types.ts';
import { type PersonOption } from '../PersonMultiSelectDropdown/types.ts';
import { type MovieFormikValues, type NumberOption, type Option, type TvFormikValues } from '../types.ts';
import { swapValuesWithLabels } from './swapValuesWithLabels.ts';

const baseTransformers = {
  companies: (values: CompanyOption[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withCompanies: values.map(value => value.id).join('|'),
    };
  },
  runtimes: (values: number[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withRuntimeGte: values[0],
      withRuntimeLte: values[1],
    };
  },
  voteAverages: (values: number[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      voteAverageGte: values[0],
      voteAverageLte: values[1],
    };
  },
  withGenres: (values: Option[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withGenres: values.map(value => value.id).join('|'),
    };
  },
  withKeywords: (values: Option[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withKeywords: values.map(value => value.id).join('|'),
    };
  },
  withoutGenres: (values: Option[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withoutGenres: values.map(value => value.id).join('|'),
    };
  },
  withoutKeywords: (values: Option[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withoutKeywords: values.map(value => value.id).join('|'),
    };
  },
};

const movieTransformers = {
  cast: (values: PersonOption[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withCast: values.map(value => value.id).join('|'),
    };
  },
  certifications: (values: number[], marks: Mark[], config: Config) => {
    if (values.length === 0) {
      return {};
    }

    const labels = swapValuesWithLabels(values, marks);

    if (labels[0] === labels[1]) {
      return {
        certification: labels[0],
        certificationCountry: config.country,
      };
    }

    return {
      certificationCountry: config.country,
      certificationGte: labels[0],
      certificationLte: labels[1],
    };
  },
  crew: (values: PersonOption[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withCrew: values.map(value => value.id).join('|'),
    };
  },
  releaseType: (values: Option[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withReleaseType: values.map(value => value.id).join('|'),
    };
  },
  releaseYears: (values: number[]) => {
    if (values.length !== 2) {
      return {};
    }

    if (!values[0] || !values[1]) {
      return {};
    }

    if (values[0] === values[1]) {
      return {
        primaryReleaseYear: values[0],
      };
    }

    return {
      primaryReleaseDateGte: format(new Date(values[0], 1, 1), 'yyyy-MM-dd'),
      primaryReleaseDateLte: format(new Date(values[1], 12, 31), 'yyyy-MM-dd'),
    };
  },
  ...baseTransformers,
};

const tvTransformers = {
  firstAired: (values: number[]) => {
    if (values.length !== 2) {
      return {};
    }

    if (!values[0] || !values[1]) {
      return {};
    }

    if (values[0] === values[1]) {
      return {
        firstAirDateYear: values[0],
      };
    }

    return {
      firstAirDateGte: format(new Date(values[0], 1, 1), 'yyyy-MM-dd'),
      firstAirDateLte: format(new Date(values[1], 12, 31), 'yyyy-MM-dd'),
    };
  },
  screenedTheatrically: (value: boolean) => {
    return {
      screenedTheatrically: value,
    };
  },
  status: (values: NumberOption[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withStatus: values.map(value => value.id).join('|'),
    };
  },
  type: (values: NumberOption[]) => {
    if (values.length === 0) {
      return {};
    }

    return {
      withType: values.map(value => value.id).join('|'),
    };
  },
  ...baseTransformers,
};

export const transformMovieValuestoFilters = (
  values: MovieFormikValues,
  filterOptions: Record<string, Mark[] | Option[]>,
  config: Config,
): DiscoverMovieFilters =>
  keys(values).reduce((acc: DiscoverMovieFilters, key) => {
    const transformer = movieTransformers[key];

    return {
      ...acc,
      // @ts-expect-error Typing needs to be improved
      ...transformer(values[key], filterOptions[key], config),
    };
  }, {});

export const transformTvValuestoFilters = (values: TvFormikValues): DiscoverTvFilters =>
  keys(values).reduce((acc: DiscoverTvFilters, key) => {
    const transformer = tvTransformers[key];

    return {
      ...acc,
      // @ts-expect-error Typing needs to be improved
      ...transformer(values[key]),
    };
  }, {});
